import * as React from "react"
import { useTranslation } from "react-i18next"
import { ArchivedChatContext } from "@/contexts/ArchivedChatContext"
import { useCallback } from "react"
import { cn } from "@/lib/utils"
import { MessageContent } from "./message"
import { Download, Loader2, Share2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  browserVersion,
  isMacOs,
  isMobile,
  isSafari,
  isWindows,
} from "@/utils/detectDevice"
import { updatePDFViewer } from "@/reducers/overlaysSlice"
import { mobileRoutes, setMobileRoute } from "@/routes/routes"
import { useAppDispatch } from "@/reducers/hooks"
import useDownloadFileName from "@/hooks/useDownloadFileName"
import useHandleDownload from "@/hooks/useHandleDownload"
import useHandleShareLink from "@/hooks/useHandleShareLink"
import CMRMetaData from "@/components/cmr/cmr-meta-data"
import useCMRMetadata from "@/hooks/useCMRMetadata"
import useMessage from "@/components/ui/message/useMessage"
import { mapFileUrlToSameOrigin } from "@/components/message/utils/mapFileToSameOrigin"
import { usePdfPageCount } from "@/hooks/usePdfPageCount"

export interface MessageImageProps
  extends React.HTMLAttributes<HTMLDivElement> {
  src?: string
  alt?: string
  showDownload?: boolean
  showShare?: boolean
  onDownload?: () => void
  onShare?: () => void
  onExternalView?: () => void
  loading?: boolean
  width?: number | string
  height?: number | string
  aspectRatio?: "square" | "video" | "auto"
  forwarded?: boolean
  reply?: boolean
}

const MessagePdf = React.forwardRef<HTMLDivElement, MessageImageProps>(
  (
    {
      className,
      src,
      alt,
      showDownload = true,
      showShare = true,
      onDownload,
      onShare,
      onExternalView,
      loading = false,
      width,
      height,
      aspectRatio = "auto",
      forwarded = false,
      reply = false,
      children,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const message = useMessage({ forwarded, reply })
    const archivedChat = React.useContext(ArchivedChatContext)
    const url = src ? src : (message?.thumbnail?.uri ?? "pdf.svg")
    const downloadFileName = useDownloadFileName(message)
    const download = useHandleDownload()
    const share = useHandleShareLink(message)
    const pageCount = usePdfPageCount(message?.url)

    const handleDownload = onDownload
      ? onDownload
      : downloadFileName && url
        ? (e: React.MouseEvent) => {
            e.stopPropagation()
            download(mapFileUrlToSameOrigin(message.url!), downloadFileName)
          }
        : undefined
    const handleShare = onShare
      ? onShare
      : downloadFileName
        ? (e: React.MouseEvent) => {
            e.stopPropagation()
            share(downloadFileName)
          }
        : undefined

    const { documentMetadata, driverContext, hasCMRMetadata } =
      useCMRMetadata(message)

    const [imageLoading, setImageLoading] = React.useState(true)
    const [imageError, setImageError] = React.useState(false)

    const handleImageLoad = () => {
      setImageLoading(false)
      setImageError(false)
    }

    const handleImageError = () => {
      setImageLoading(false)
      setImageError(true)
    }

    const handleExternalView = (e: React.MouseEvent) => {
      e.stopPropagation()
      if (onExternalView) {
        onExternalView()
      } else {
        window.open(url, "_blank")
      }
    }

    const handlePDFClick = useCallback(
      (event: React.MouseEvent) => {
        const isOldSafari = isSafari && parseInt(browserVersion, 10) < 17
        const isCtrlOrCmdClick =
          event.button === 0 &&
          ((isWindows && event.ctrlKey) || (isMacOs && event.metaKey))
        const isMiddleClick = event.button === 1
        if (isCtrlOrCmdClick || isMiddleClick || isOldSafari) {
          window.open(message.url)
        } else {
          dispatch(
            updatePDFViewer({
              visible: true,
              PDFMessage: message,
              externalSource: archivedChat?.mediaSource,
            }),
          )
          if (isMobile && !archivedChat) {
            setMobileRoute(mobileRoutes.PDF)
          }
        }
      },
      [archivedChat, dispatch, message],
    )
    const getAspectRatioClass = () => {
      switch (aspectRatio) {
        case "square":
          return "aspect-square"
        case "video":
          return "aspect-video"
        default:
          return ""
      }
    }

    const imageElement = (
      <div
        className={cn(
          "group/image relative max-w-64 overflow-hidden rounded-lg bg-muted",
          getAspectRatioClass(),
          className,
        )}
        style={{ width, height }}
      >
        {(loading || imageLoading) && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {imageError ? (
          <div className="absolute inset-0 flex items-center justify-center bg-muted text-muted-foreground">
            <div className="text-center">
              <div className="text-sm">{t("chat.media.imageLoadFailed")}</div>
              <Button
                variant="link"
                size="sm"
                onClick={handleExternalView}
                className="h-auto p-0 text-xs"
              >
                {t("chat.media.tryOpeningExternally")}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <img
              src={url}
              alt={alt ?? t("chat.media.pdfAlt")}
              className={cn(
                "h-full w-full object-cover transition-opacity",
                imageLoading && "opacity-0",
              )}
              onLoad={handleImageLoad}
              onError={handleImageError}
              loading="lazy"
            />
            {/* PDF icon overlay */}
            <div className="absolute left-2 top-2 flex items-center gap-1 rounded bg-black/60 px-1.5 py-1">
              <img src="/pdf.svg" alt="PDF" className="h-5 w-5" />
              {pageCount !== null && (
                <span className="text-xs font-medium text-white">
                  {t("chat.media.pageCount", { count: pageCount })}
                </span>
              )}
            </div>
          </>
        )}

        {/* Hover overlay with actions */}
        {!imageError && !loading && !imageLoading && (
          <div
            data-testid="pdf-file"
            className="absolute inset-0 cursor-pointer bg-black/0 transition-colors group-hover/image:bg-black/20"
            onClick={handlePDFClick}
          >
            <div
              className={`absolute bottom-2 right-2 flex gap-2 transition-opacity ${
                isMobile
                  ? "opacity-100"
                  : "opacity-0 group-hover/image:opacity-100"
              }`}
            >
              {showShare && handleShare && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 bg-background dark:bg-input/30"
                  onClick={handleShare}
                >
                  <Share2 className="h-4 w-4" />
                </Button>
              )}
              {showDownload && handleDownload && (
                <>
                  {isMobile ? (
                    <a
                      href={mapFileUrlToSameOrigin(message.url!)}
                      onClick={(e) => e.stopPropagation()}
                      download
                      className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-background hover:bg-accent hover:text-accent-foreground dark:bg-input/30"
                    >
                      <Download className="h-4 w-4" />
                    </a>
                  ) : (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 bg-background dark:bg-input/30"
                      onClick={handleDownload}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    )

    return (
      <MessageContent ref={ref} {...props}>
        <div
          className={`${isMobile ? "flex-col-reverse gap-4" : "gap-2"} flex items-start`}
        >
          {hasCMRMetadata && (
            <div className="flex-shrink-0">
              <CMRMetaData
                message={message}
                metadata={documentMetadata?.data}
                driverContext={driverContext}
                documentType={documentMetadata?.documentType}
                blurry={documentMetadata?.blurry}
                remarks={documentMetadata?.remarks}
              />
            </div>
          )}
          {imageElement}
        </div>
        {children}
      </MessageContent>
    )
  },
)

MessagePdf.displayName = "MessagePdf"

export { MessagePdf }
